// ─────────────────────────────────────────────────────────────────────────────
// PageHeader — contextual header rendered inside the AppBar
// ─────────────────────────────────────────────────────────────────────────────
//
// Used by detail/editor pages (e.g. the rule builder) to surface page-specific
// context in the top bar instead of a generic page-type label. Driven by the
// `pageHeader` Redux slice — set via dispatch(setPageHeader({...})), clear on
// unmount via dispatch(clearPageHeader()).
//
// Layout: [breadcrumbs] / [title]   [status badge]
// ─────────────────────────────────────────────────────────────────────────────

import ChevronRight from "@mui/icons-material/ChevronRight";
import { Box, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

import type { PageHeaderBreadcrumb, PageHeaderStatusBadge } from "../../../redux/pageHeaderSlice";

interface PageHeaderProps {
	title: string;
	breadcrumbs?: PageHeaderBreadcrumb[];
	statusBadge?: PageHeaderStatusBadge | null;
}

const STATUS_TONE_STYLES: Record<PageHeaderStatusBadge["tone"], { bg: string; color: string; dot: string }> = {
	active:   { bg: "rgba(39, 174, 96, 0.12)",  color: "#1B7D44", dot: "#27AE60" },
	inactive: { bg: "rgba(13, 31, 48, 0.08)",   color: "#475662", dot: "#8C9AA6" },
	draft:    { bg: "rgba(245, 192, 32, 0.16)", color: "#7A5C00", dot: "#E0AA00" },
	archived: { bg: "rgba(220, 38, 38, 0.10)",  color: "#7E1A19", dot: "#DC2626" }
};

const StatusBadge = ({ badge }: { badge: PageHeaderStatusBadge }) => {
	const { bg, color, dot } = STATUS_TONE_STYLES[badge.tone];
	return (
		<Box
			component="span"
			sx={{
				display: "inline-flex",
				alignItems: "center",
				gap: 0.75,
				px: 1.25,
				py: 0.375,
				borderRadius: 999,
				background: bg,
				color,
				fontSize: 12,
				fontWeight: 600,
				letterSpacing: "0.02em",
				lineHeight: 1.3
			}}
		>
			<Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%", background: dot }} />
			{badge.label}
		</Box>
	);
};

const Breadcrumbs = ({ items }: { items: PageHeaderBreadcrumb[] }) => (
	<Stack direction="row" spacing={0.5} sx={{ minHeight: 16, alignItems: "center" }}>
		{items.map((crumb, i) => {
			const isLast = i === items.length - 1;
			const linkSx = {
				color: isLast ? "rgba(13, 31, 48, 0.55)" : "rgba(13, 31, 48, 0.45)",
				fontSize: 12,
				fontWeight: 500,
				letterSpacing: "0.02em",
				textDecoration: "none",
				transition: "color 0.18s",
				...(crumb.href ? { "&:hover": { color: "#02D5AB" } } : {})
			};
			const node = crumb.href ? (
				<RouterLink key={`${i}-${crumb.label}`} to={crumb.href} style={{ textDecoration: "none" }}>
					<Box component="span" sx={linkSx}>{crumb.label}</Box>
				</RouterLink>
			) : (
				<Box key={`${i}-${crumb.label}`} component="span" sx={linkSx}>{crumb.label}</Box>
			);
			return (
				<Box key={`wrap-${i}`} component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 0.5 }}>
					{node}
					{!isLast && (
						<ChevronRight sx={{ fontSize: 14, color: "rgba(13, 31, 48, 0.30)" }} aria-hidden="true" />
					)}
				</Box>
			);
		})}
	</Stack>
);

export default function PageHeader({ title, breadcrumbs = [], statusBadge = null }: PageHeaderProps) {
	return (
		<Stack direction="row" spacing={2} sx={{ minWidth: 0, flex: 1, alignItems: "center" }}>
			<Box sx={{ minWidth: 0, flex: 1 }}>
				{breadcrumbs.length > 0 && <Breadcrumbs items={breadcrumbs} />}
				<Typography
					variant="h5"
					sx={{
						fontWeight: 700,
						fontSize: 26,
						letterSpacing: "-0.012em",
						color: "rgba(13, 31, 48, 1)",
						lineHeight: 1.2,
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
				>
					{title}
				</Typography>
			</Box>
			{statusBadge && <StatusBadge badge={statusBadge} />}
		</Stack>
	);
}
